import { Cookies } from "react-cookie";
import BrandclubStoreConfigUtils from "../utils/brandclubStoreConfigUtils.js";
const CONSENT_STORAGE_KEY = "bc_tracking_consent";
const CONSENT_COOKIE_NAME = "bc_t_c";
let userConsent = null;
let consentListenerAttached = false;
let debug = false;
const cookies = new Cookies();
const registeredProviders = [];
const log = (...args) => {
  if (!debug) return;
  console.info(...["[consent-manager]"].concat(args));
};
const warn = (...args) => {
  if (!debug) return;
  console.warn(...["[consent-manager]"].concat(args));
};
function loadConsentFromStorage() {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const cookieValue = cookies.get(CONSENT_COOKIE_NAME);
    if (cookieValue !== void 0) {
      log("Loaded consent from cookie:", cookieValue);
      return cookieValue === "true" || cookieValue === true;
    }
    if (typeof localStorage !== "undefined") {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored !== null) {
        log("Loaded consent from localStorage (fallback):", stored);
        const consentValue = stored === "true";
        saveConsentToCookie(consentValue);
        return consentValue;
      }
    }
    return null;
  } catch (e) {
    warn("Failed to load consent from storage:", e);
    return null;
  }
}
function saveConsentToCookie(consent) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const cookieDomain = "." + BrandclubStoreConfigUtils.getCookieDomain();
    cookies.set(CONSENT_COOKIE_NAME, String(consent), {
      domain: cookieDomain,
      maxAge: 365 * 24 * 60 * 60,
      // 1 year
      path: "/",
      secure: true,
      sameSite: "lax"
    });
    log(`Saved consent to cookie (${cookieDomain}):`, consent);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(CONSENT_STORAGE_KEY, String(consent));
    }
  } catch (e) {
    warn("Failed to save consent to cookie:", e);
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(CONSENT_STORAGE_KEY, String(consent));
        log("Saved consent to localStorage (fallback):", consent);
      }
    } catch (fallbackError) {
      warn("Failed to save consent to localStorage fallback:", fallbackError);
    }
  }
}
function getConsentFromDataLayer() {
  var _a;
  if (typeof window === "undefined" || !Array.isArray(window.dataLayer)) {
    return null;
  }
  try {
    for (let i = window.dataLayer.length - 1; i >= 0; i--) {
      const item = window.dataLayer[i];
      if ((item == null ? void 0 : item.event) === "consent_update" && (item == null ? void 0 : item.analytics_consent)) {
        return item.analytics_consent === "granted";
      }
      if ((_a = item == null ? void 0 : item.consent) == null ? void 0 : _a.analytics) {
        return item.consent.analytics === "granted";
      }
      if ((item == null ? void 0 : item.event) === "consent" && (item == null ? void 0 : item.ad_storage)) {
        return item.ad_storage === "granted";
      }
      if ((item == null ? void 0 : item.event) === "consent" && (item == null ? void 0 : item.marketing)) {
        return item.marketing === "granted";
      }
      if ((item == null ? void 0 : item.event) === "OneTrustGroupsUpdated" && typeof window !== "undefined") {
        const oneTrustActiveGroups = window.OnetrustActiveGroups;
        if (oneTrustActiveGroups) {
          return oneTrustActiveGroups.includes("C0004") || oneTrustActiveGroups.includes("C0002");
        }
      }
    }
  } catch (e) {
    warn("Error reading consent from dataLayer:", e);
  }
  return null;
}
function attachConsentListener() {
  if (consentListenerAttached || typeof window === "undefined") {
    return;
  }
  try {
    window.dataLayer = window.dataLayer || [];
    const originalPush = window.dataLayer.push;
    window.dataLayer.push = function(...args) {
      const result = originalPush.apply(window.dataLayer, args);
      args.forEach((item) => {
        var _a;
        let consentGranted = null;
        if ((item == null ? void 0 : item.event) === "consent_update" && (item == null ? void 0 : item.analytics_consent)) {
          consentGranted = item.analytics_consent === "granted";
        } else if ((_a = item == null ? void 0 : item.consent) == null ? void 0 : _a.analytics) {
          consentGranted = item.consent.analytics === "granted";
        } else if ((item == null ? void 0 : item.event) === "consent" && (item == null ? void 0 : item.ad_storage)) {
          consentGranted = item.ad_storage === "granted";
        } else if ((item == null ? void 0 : item.event) === "consent" && (item == null ? void 0 : item.marketing)) {
          consentGranted = item.marketing === "granted";
        } else if ((item == null ? void 0 : item.event) === "OneTrustGroupsUpdated" && typeof window !== "undefined") {
          const oneTrustActiveGroups = window.OnetrustActiveGroups;
          if (oneTrustActiveGroups) {
            consentGranted = oneTrustActiveGroups.includes("C0004") || oneTrustActiveGroups.includes("C0002");
          }
        }
        if (consentGranted !== null) {
          log("Consent update detected from dataLayer:", consentGranted);
          handleConsentChange(consentGranted);
        }
      });
      return result;
    };
    consentListenerAttached = true;
    log("Attached dataLayer consent listener");
  } catch (e) {
    warn("Failed to attach consent listener:", e);
  }
}
function handleConsentChange(granted) {
  const previousConsent = userConsent;
  userConsent = granted;
  saveConsentToCookie(granted);
  if (previousConsent !== granted) {
    log("Consent changed from", previousConsent, "to", granted);
    registeredProviders.forEach((provider) => {
      try {
        if (granted && provider.grantConsent) {
          provider.grantConsent();
          log("Called grantConsent on provider");
        } else if (!granted && provider.revokeConsent) {
          provider.revokeConsent();
          log("Called revokeConsent on provider");
        }
      } catch (e) {
        warn("Error calling provider consent method:", e);
      }
    });
  }
}
function registerProvider(provider) {
  if (!registeredProviders.includes(provider)) {
    registeredProviders.push(provider);
    log("Registered tracking provider for consent updates");
    if (userConsent !== null) {
      try {
        if (userConsent && provider.grantConsent) {
          provider.grantConsent();
        } else if (!userConsent && provider.revokeConsent) {
          provider.revokeConsent();
        }
      } catch (e) {
        warn("Error calling provider consent method on registration:", e);
      }
    }
  }
}
function initializeConsent() {
  if (userConsent === null) {
    const dataLayerConsent = getConsentFromDataLayer();
    if (dataLayerConsent !== null) {
      userConsent = dataLayerConsent;
    } else {
      const storedConsent = loadConsentFromStorage();
      userConsent = storedConsent;
    }
    log("Initialized consent:", userConsent);
  }
  attachConsentListener();
}
function hasConsent() {
  initializeConsent();
  return userConsent === true;
}
function setConsent(granted) {
  log("Setting consent to:", granted);
  initializeConsent();
  handleConsentChange(granted);
  if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: "consent_update",
      analytics_consent: granted ? "granted" : "denied",
      ad_storage: granted ? "granted" : "denied"
    });
  }
}
function enableDebug() {
  debug = true;
  log("Debug mode enabled for consent manager");
}
export {
  enableDebug,
  hasConsent,
  registerProvider,
  setConsent
};
//# sourceMappingURL=consent.js.map
